import React from 'react';
import { Pressable, View } from 'react-native';
import { format } from 'date-fns';
import { calcMessageGrouping } from '@sendbird/uikit-utils';
import { Text } from '@shared/components/text';
import { useI18n } from '@shared/i18n/useI18n';
import { CHAT_BUBBLE } from '../../constants';
import type { TChatTextMessageProps } from '../../types';
import { adaptPrevMessageForNameGrouping } from '../../utils/messageGrouping';
import { ChatAttachmentLabel } from './ChatAttachmentLabel';
import { ChatMessageText } from './ChatMessageText';

export const ChatTextMessage = ({
    message,
    attachment,
    prevMessage,
    isMine,
    onPress,
    onLongPress,
}: TChatTextMessageProps) => {
    const { t } = useI18n();
    const bubble = CHAT_BUBBLE[isMine ? 'outgoing' : 'incoming'];
    const [topLeft, topRight, bottomRight, bottomLeft] = bubble.radius;
    const text = message.isUserMessage() ? message.message : '';

    const showName = !calcMessageGrouping(
        true,
        message,
        adaptPrevMessageForNameGrouping(message, prevMessage),
        undefined,
    ).groupWithPrev;

    return (
        <View className="px-md mb-3">
            <View className={isMine ? 'items-end' : 'items-start'}>
                <View className={`max-w-[80%] gap-1 ${isMine ? 'items-end' : 'items-start'}`}>
                    {showName && (
                        <Text numberOfLines={1} className="text-sm font-semibold">
                            {isMine ? t('screens.chat.me') : message.sender?.nickname}
                        </Text>
                    )}
                    <Pressable
                        onPress={onPress}
                        onLongPress={onLongPress}
                        style={{
                            opacity: attachment?.isOpening ? CHAT_BUBBLE.openingOpacity : 1,
                            backgroundColor: bubble.background,
                            borderColor: bubble.border,
                            borderWidth: CHAT_BUBBLE.borderWidth,
                            borderTopLeftRadius: topLeft,
                            borderTopRightRadius: topRight,
                            borderBottomRightRadius: bottomRight,
                            borderBottomLeftRadius: bottomLeft,
                            paddingHorizontal: CHAT_BUBBLE.paddingHorizontal,
                            paddingVertical: CHAT_BUBBLE.paddingVertical,
                        }}
                    >
                        {attachment ? (
                            <ChatAttachmentLabel attachment={attachment} />
                        ) : (
                            <ChatMessageText message={message} text={text} onLongPress={onLongPress} />
                        )}
                    </Pressable>
                    <Text className="text-xs text-gray-500">{format(message.createdAt, 'HH:mm')}</Text>
                </View>
            </View>
        </View>
    );
};
